import {
    Button,
    Alert,
    Collapse,
    Grid,
    LinearProgress,
    Typography,
    TextField,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
} from "@mui/material";
import { DataGrid, GridColDef, deDE, GridRowParams, GridRenderCellParams } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState } from "react";
import { CustomSnackbar } from "../../components/core/Snackbar";
import { useFetch, uploadFetch, deleteFetch } from "../../hooks/useFetch";
import { CustomerEdit } from "../../components/customer/CustomerEdit";

import { IFeedback } from "../../interfaces/IFeedback";
import { ICustomer } from "../../interfaces/ICustomer";
import { IInsurance } from "../../interfaces/IInsurance";
import { IGender } from "../../interfaces/IGender";
import { ITitle } from "../../interfaces/ITitle";
import { IAddress, IAddressType } from "../../interfaces/IAddress";
import { getEmptyCustomer } from "../../components/customer/CustomerEdit";
import { IPropertyManagment } from "../../interfaces/IPropertyManagement";
import { Delete } from "@mui/icons-material";
import ValidationDialog from "../ValidationDialog";
import { CustomerFromVCard } from "../../components/customer/CustomerFromVCard";

export const getEmptyAddress = (addressType: number): IAddress => {
    return {
        id: -1,
        Address_Type: addressType,
        street: "",
        street_number: "",
        Postcode: null,
    };
};

export const CustomerOverview: React.FC = () => {
    const [feedback, setFeedback] = useState<IFeedback>({
        open: false,
        message: "",
        severity: "info",
    });

    const [customerData, setCustomerData, statusCode] = useFetch<ICustomer[]>("/customer/");
    const [insuranceData, setInsuranceCodeData] = useFetch<IInsurance[]>("/insurance/");
    const [propertyManagementData, setPropertyManagementData] = useFetch<IPropertyManagment[]>("/property_management/");
    const [genderData] = useFetch<IGender[]>("/customer/gender/");
    const [titleData] = useFetch<ITitle[]>("/customer/academic-title/");
    const [addressTypeArray] = useFetch<IAddressType[]>("/address/types/");

    const [editCustomer, setEditCustomer] = useState(getEmptyCustomer());
    const [wasSuccessfully, setWasSuccessfully] = useState(true);
    const [isLoadgin, setIsLoading] = useState(false);
    const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");

    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
    const [customerIdToDelete, setCustomerIdToDelete] = React.useState<number | null>(null);
    const is_superuser = localStorage.getItem("is_superuser");

    const [areRequiredFieldsSet, setAreRequiredFieldsSet] = useState(false);
    const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);
    const [proceedWithSave, setProceedWithSave] = useState(false);
    const [missingFields, setMissingFields] = useState<string[]>([]);

    const handleValidationDialogClose = () => {
        setIsValidationDialogOpen(false);
        setProceedWithSave(false);
    };

    const handleValidationDialogProceed = () => {
        setIsValidationDialogOpen(false);
        setProceedWithSave(true);
        handleSave();
    };

    const openDeleteConfirmationDialog = (projectId: number) => {
        setCustomerIdToDelete(projectId);
        setOpenDeleteDialog(true);
    };

    const closeDeleteConfirmationDialog = () => {
        setCustomerIdToDelete(null);
        setOpenDeleteDialog(false);
    };

    const handleRemove = (customerId: number) => {
        if (is_superuser === "true") {
            deleteFetch("/customer/", customerId, setWasSuccessfully);
            setFeedback({
                open: true,
                message: "Kunde erfolgreich gelöscht!",
                severity: "success",
            });
            const newArray =
                customerData === undefined ? "" : customerData.filter((customer) => customer.id !== customerId);
            setCustomerData(newArray);
            closeDeleteConfirmationDialog();
        } else {
        }
    };

    const columns: GridColDef[] = [
        {
            field: "customerNumber",
            headerName: "Nr.",
            valueGetter: (params) => params.row.id,
            flex: 1,
        },
        {
            field: "firstname",
            headerName: "Vorname",
            valueGetter: (params) => params.row.firstname,
            flex: 2,
        },
        {
            field: "lastname",
            headerName: "Name",
            valueGetter: (params) => params.row.lastname,
            flex: 2,
        },
        {
            field: "telefon",
            headerName: "Telefon",
            valueGetter: (params) => params.row.telefon,
            flex: 2,
        },
        {
            field: "mobil",
            headerName: "Mobil",
            valueGetter: (params) => params.row.mobil,
            flex: 2,
        },
        {
            field: "email",
            headerName: "Email",
            valueGetter: (params) => params.row.email,
            flex: 3,
        },
        {
            field: "action",
            headerName: "Aktionen",
            renderCell: (params: GridRenderCellParams<any, number>) => {
                return (
                    <>
                        {" "}
                        {(is_superuser == "true" || true) && (
                            <>
                                <IconButton
                                    style={{ position: "relative" }}
                                    size="small"
                                    onClick={() => openDeleteConfirmationDialog(params.row.id)}
                                >
                                    <Delete />
                                </IconButton>
                                <Dialog
                                    open={openDeleteDialog && customerIdToDelete === params.row.id}
                                    onClose={closeDeleteConfirmationDialog}
                                >
                                    <DialogTitle>Bestätigung der Löschung</DialogTitle>
                                    <DialogContent>
                                        Sind Sie sicher, dass Sie "{params.row["firstname"]} {params.row["lastname"]}"
                                        löschen wollen?
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={closeDeleteConfirmationDialog} color="error">
                                            Abbruch
                                        </Button>
                                        <Button onClick={() => handleRemove(params.row.id)} color="primary">
                                            Bestätigen
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                            </>
                        )}
                    </>
                );
            },
            flex: 1,
        },
    ];

    const handleChangeId = (params: GridRowParams) => {
        const customer = customerData?.find((x) => x.id === params.id) ?? getEmptyCustomer();
        setEditCustomer(customer);
        setIsAddCustomerOpen(true);
    };

    const addNewCustomer = () => {
        setEditCustomer(getEmptyCustomer());
        setIsAddCustomerOpen(true);
    };

    const handleAfterSave = (savedObject: ICustomer) => {
        if (customerData !== undefined) {
            setFeedback({ open: true, message: "Gespeichert!", severity: "success" });

            setCustomerData((prev: ICustomer[]) =>
                prev.some((x) => x.id === savedObject.id)
                    ? prev.map((x) => (x.id === savedObject.id ? savedObject : x))
                    : [...prev, savedObject],
            );

            setIsAddCustomerOpen(false);
        }
    };

    const handleSave = () => {
        if (!areRequiredFieldsSet && !proceedWithSave) {
            setIsValidationDialogOpen(true);
            return;
        }

        let uploadObject = {
            ...editCustomer,
            Address: editCustomer.b2b ? undefined : editCustomer.Address,
            Company: editCustomer.b2b ? editCustomer.Company : undefined,
        } as ICustomer;

        setWasSuccessfully(true);
        if (editCustomer.id === 0) {
            uploadFetch("/customer/", true, uploadObject, handleAfterSave, setWasSuccessfully, () => {}, setIsLoading);
        } else {
            uploadFetch(
                `/customer/${editCustomer.id}`,
                false,
                uploadObject,
                handleAfterSave,
                setWasSuccessfully,
                () => {},
                setIsLoading,
            );
        }

        setProceedWithSave(false);
    };

    const handleSubmit = (e: React.SyntheticEvent) => {
        e.preventDefault();
        handleSave();
    };

    useEffect(() => {
        if (!wasSuccessfully) {
            setFeedback({
                open: true,
                message: "Ein fehler ist aufgetretten!",
                severity: "error",
            });
            setIsLoading(false);
        }
    }, [wasSuccessfully]);

    const filteredCustomers = useMemo(() => {
        if (!customerData) return [];
        if (searchTerm === "") return customerData;

        return customerData.filter((x) =>
            [x.firstname, x.lastname, x.email, x.telefon, x.mobil].some((field) =>
                String(field).toLowerCase().includes(searchTerm.toLowerCase()),
            ),
        );
    }, [customerData, searchTerm]);

    if (statusCode !== null && statusCode !== 200) {
        return <Alert severity="error">Fehler!</Alert>;
    } else if (customerData === undefined) {
        return <LinearProgress />;
    } else {
        return (
            <>
                <Typography variant="h5">
                    Kundenverzeichnis
                    {true && (
                        <Button sx={{ float: "right" }} variant="outlined" onClick={addNewCustomer}>
                            Kunden hinzufügen
                        </Button>
                    )}
                    <CustomerFromVCard
                        handleReturnCustomer={(editCustomer: ICustomer) => {
                            setEditCustomer(editCustomer);
                            setIsAddCustomerOpen(true);
                        }}
                        setIsAddCustomerOpen={setIsAddCustomerOpen}
                        buttonTitle={"über VCARD"}
                    />
                </Typography>

                <Collapse sx={{ mt: 1 }} in={isLoadgin}>
                    <LinearProgress />
                </Collapse>

                <ValidationDialog
                    open={isValidationDialogOpen}
                    handleClose={handleValidationDialogClose}
                    handleProceed={handleValidationDialogProceed}
                    missingFields={missingFields}
                />

                <CustomSnackbar feedback={feedback} setFeedback={setFeedback} />

                <TextField
                    sx={{ mt: 2, mb: 2 }}
                    label="Suche"
                    fullWidth
                    value={searchTerm}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
                />
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item sm={isAddCustomerOpen ? 6 : 12}>
                        <Box sx={{ height: "80vh", overflow: "auto" }}>
                            <DataGrid
                                localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
                                rows={filteredCustomers}
                                columns={columns}
                                onRowClick={(params, event, details) => {
                                    if (true) {
                                        const target = event.target as HTMLElement;
                                        const isDeleteIconClicked = target.closest(".MuiIconButton-root") !== null;
                                        if (isDeleteIconClicked) {
                                            event.stopPropagation();
                                            openDeleteConfirmationDialog(Number(params.id));
                                        } else {
                                            handleChangeId(params);
                                        }
                                    }
                                }}
                                autoHeight
                                initialState={{
                                    sorting: {
                                        sortModel: [{ field: "firstname", sort: "asc" }],
                                    },
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item sm={isAddCustomerOpen ? 6 : 0}>
                        <Collapse in={isAddCustomerOpen}>
                            <form onSubmit={handleSubmit}>
                                <Typography variant="h6">
                                    Kunde
                                    <Button
                                        sx={{ float: "right", ml: 2 }}
                                        type="submit"
                                        variant="contained"
                                        disabled={isLoadgin}
                                    >
                                        Speichern
                                    </Button>
                                    <Button
                                        sx={{ float: "right" }}
                                        onClick={() => setIsAddCustomerOpen(false)}
                                        variant="outlined"
                                    >
                                        Abbruch
                                    </Button>
                                </Typography>
                                {isAddCustomerOpen && (
                                    <CustomerEdit
                                        key={`editCustomer-${editCustomer.id}`}
                                        insuranceArray={insuranceData || []}
                                        setInsuranceArray={setInsuranceCodeData}
                                        propertyManagementArray={propertyManagementData || []}
                                        setPropertyManagementArray={setPropertyManagementData}
                                        genderArray={genderData || []}
                                        titleArray={titleData || []}
                                        addressTypeData={addressTypeArray || []}
                                        customerObject={editCustomer}
                                        setCustomerObject={setEditCustomer}
                                        areRequiredFieldsSet={areRequiredFieldsSet}
                                        setAreRequiredFieldsSet={setAreRequiredFieldsSet}
                                        setMissingFields={setMissingFields} //
                                    />
                                )}
                            </form>
                        </Collapse>
                    </Grid>
                </Grid>
            </>
        );
    }
};
